import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { take } from 'rxjs/operators';
import { AppService } from '../../../app.service';
import {
  AdminGridCallbackEvent,
  AdminGridConfig,
  AdminGridDirective,
} from '../../shared/admin-grid.directive';
import { computeFixedDropdownPosition } from '../admin-users/admin-users.component';
import { AuditEngineService } from './audit-engine.service';

interface AuditDataType {
  label: string;
  value: number;
}

interface UserListOption {
  value: string;
  name: string;
}

/**
 * Angular 12 migration of admin.audit-engine.
 *
 * Two audit modes — each hits a different gizmoGrid endpoint:
 *   - Date Range Audit (audit_type=1): loadGridJson?action_code=wvs4c4&grid_id=225
 *       extraParams: { sso_id, start_dt, end_dt }
 *   - User Audit       (audit_type=2): loadGridJson?action_code=k9p9st&grid_id=224
 *       extraParams: { sso_id, user_sso_id }
 *
 * Users dropdown is populated from loadJsonObject?action_code=652qui.
 *
 * Grid does not auto-load — the user must pick filters and click Go. Each Go
 * re-mounts the AdminGridDirective so the new actionCode/gridId/extraParams
 * are picked up cleanly and gizmoGrid state is reset.
 *
 * Legacy reference: GTW12/ui/app/components/admin/auditEngine/auditCtrl.js
 */
@Component({
  selector: 'gtw-audit-engine',
  templateUrl: './audit-engine.component.html',
  styleUrls: ['./audit-engine.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AuditEngineComponent implements OnInit {
  @ViewChild(AdminGridDirective) adminGrid?: AdminGridDirective;

  readonly audit_data_types: AuditDataType[] = [
    { label: 'Date Range Audit Data', value: 1 },
    { label: 'User Audit Data', value: 2 },
  ];

  filter: { audit_type: number } = { audit_type: 1 };
  dateFrom = '';
  dateTo = '';
  selectedUser = '';

  userList: any[] = [];
  userListOptions: UserListOption[] = [];
  filteredUserOptions: UserListOption[] = [];

  /** Searchable single-select dropdown (gt-multiselect visual). */
  userMenuOpen = false;
  userSearchText = '';
  userMenuStyle: { [key: string]: string } = {};
  loadingUsers = false;

  gridConfig?: AdminGridConfig;
  showGrid = false;
  loading = false;
  gridError = '';

  logged_in_user = '';

  constructor(
    private cdr: ChangeDetectorRef,
    private appService: AppService,
    private auditEngineService: AuditEngineService,
  ) {}

  ngOnInit(): void {
    this.logged_in_user = this.appService.USER_SETTINGS?.user?.sso_id || '';
  }

  // ---------------------------------------------------------------------------
  // Filter bar events
  // ---------------------------------------------------------------------------

  onAuditChange(): void {
    this.showGrid = false;
    this.gridError = '';
    this.userMenuOpen = false;
    if (this.filter.audit_type === 2 && this.userList.length === 0) {
      this.loadUserNames();
    }
    this.cdr.detectChanges();
  }

  // ---------------------------------------------------------------------------
  // User picker (single-select with search — gt-multiselect visual)
  // ---------------------------------------------------------------------------

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.userMenuOpen) {
      this.userMenuOpen = false;
      this.cdr.detectChanges();
    }
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  onViewportChange(): void {
    if (this.userMenuOpen) {
      this.userMenuOpen = false;
      this.cdr.detectChanges();
    }
  }

  toggleUserMenu(event: Event): void {
    event.stopPropagation();
    this.userMenuOpen = !this.userMenuOpen;
    if (this.userMenuOpen) {
      this.userMenuStyle = computeFixedDropdownPosition(event.currentTarget as HTMLElement);
      // Always show the full list when reopening — don't carry stale search.
      this.userSearchText = '';
      this.filteredUserOptions = [...this.userListOptions];
    }
    this.cdr.detectChanges();
  }

  onUserSearch(): void {
    const term = (this.userSearchText || '').toLowerCase();
    this.filteredUserOptions = term
      ? this.userListOptions.filter(
          (u) => u.name.toLowerCase().includes(term) || u.value.toLowerCase().includes(term),
        )
      : [...this.userListOptions];
    this.cdr.detectChanges();
  }

  selectUser(user: UserListOption | null, event: Event): void {
    event.stopPropagation();
    this.selectedUser = user?.value ?? '';
    this.userMenuOpen = false;
    this.gridError = '';
    this.cdr.detectChanges();
  }

  get selectedUserLabel(): string {
    if (!this.selectedUser) return '';
    const match = this.userListOptions.find((u) => u.value === this.selectedUser);
    return match ? match.name : this.selectedUser;
  }

  go(): void {
    if (this.filter.audit_type === 2 && !this.selectedUser) {
      this.gridError = 'Please select a user.';
      this.showGrid = false;
      this.cdr.detectChanges();
      return;
    }
    this.gridError = '';

    const isUserAudit = this.filter.audit_type === 2;
    const actionCode = isUserAudit ? 'k9p9st' : 'wvs4c4';
    const gridId = isUserAudit ? '224' : '225';
    const extraParams: Record<string, any> = isUserAudit
      ? {
          sso_id: this.logged_in_user,
          user_sso_id: this.selectedUser,
        }
      : {
          sso_id: this.logged_in_user,
          start_dt: this.toApiDate(this.dateFrom),
          end_dt: this.toApiDate(this.dateTo),
        };

    this.gridConfig = {
      actionCode,
      gridId,
      extraParams,
      autoLoad: true,
    };

    // Re-mount the directive on every Go so the new actionCode/gridId and
    // extraParams are applied cleanly and gizmoGrid starts from a fresh
    // state. Without the toggle, switching audit modes would leave stale
    // header/column-template state from the previous mode in the grid.
    this.showGrid = false;
    this.cdr.detectChanges();
    setTimeout(() => {
      this.showGrid = true;
      this.cdr.detectChanges();
    }, 0);
  }

  // ---------------------------------------------------------------------------
  // Grid events
  // ---------------------------------------------------------------------------

  onGridCallback(_event: AdminGridCallbackEvent): void {
    // Legacy auditCtrl exposed ctrlFn as a no-op — no custom row/form events
    // are emitted by either audit grid.
  }

  onLoadingStateChange(state: boolean): void {
    this.loading = state;
    this.cdr.detectChanges();
  }

  // ---------------------------------------------------------------------------
  // Data
  // ---------------------------------------------------------------------------

  /**
   * Convert an `<input type="date">` value (`yyyy-MM-dd`) to the
   * `MM/DD/YYYY` format the legacy audit endpoints (`action_code=wvs4c4`,
   * `k9p9st`) were built around. Empty input → empty string (parity with
   * legacy `dateFilter.selected?.VALUE : ''`).
   */
  private toApiDate(iso: string): string {
    if (!iso) return '';
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!match) return iso;
    const [, yyyy, mm, dd] = match;
    return `${mm}/${dd}/${yyyy}`;
  }

  private loadUserNames(): void {
    this.loadingUsers = true;
    this.cdr.detectChanges();
    this.auditEngineService.getAllUserNames()
      .pipe(take(1))
      .subscribe({
        next: (data: any) => {
          this.userList = data?.jsonObject ?? [];
          this.userListOptions = this.userList.map((u: any) => ({
            value: u.SSOID,
            name: (u.SSOID || '') + ' - ' + (u.USERNAME || ''),
          }));
          this.filteredUserOptions = [...this.userListOptions];
          this.loadingUsers = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.userList = [];
          this.userListOptions = [];
          this.filteredUserOptions = [];
          this.loadingUsers = false;
          this.cdr.detectChanges();
        },
      });
  }
}
